import React, { useContext, useEffect, useRef, useState } from "react";
import { CategoryContext } from "./CategoryContext";
import AppSettings from "../../../Settings/AppSettings";
import ServiceWrapper from "../../../Services/ServiceWrapper";
import AuthManager from "../../../Common/AuthManager";
import { Link, useNavigate } from "react-router";
import { toast } from "Components/DesignSystem/Toast/useToast";
import { AppContext } from "../../../Context/AppContext";
import SkeletonElement from "./Shimmer/SkeletonElement";
import Utils from "../../../Common/Utils";
import Alert from "Components/DesignSystem/Alert/Alert";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import Breadcrumbs from "Components/DesignSystem/Breadcrumbs/Breadcrumbs";
import Button from "Components/DesignSystem/Button/Button";
import Card from "Components/DesignSystem/Card/Card";
import Dialog from "Components/DesignSystem/Dialog/Dialog";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import Menu from "Components/DesignSystem/Menu/Menu";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import NoDataIllustration from "Components/DesignSystem/EmptyState/NoDataIllustration";
import TextField from "Components/DesignSystem/TextField/TextField";
import Textarea from "Components/DesignSystem/Textarea/Textarea";
import Typography from "Components/DesignSystem/Typography/Typography";
import ImagePlaceholder from "Components/DesignSystem/ImagePlaceholder/ImagePlaceholder";
import { Copy, MoreVertical } from "Components/DesignSystem/Iconography/Icons";
import { FOCUS_RING } from "Components/DesignSystem/focusRing";
import { useDismissable } from "Components/DesignSystem/useDismissable";

/**
 * Category create/edit form and detail view, on the design system since the
 * category module's Phase 3 — every service call, permission check (including
 * the § 7 must-not-regress `AuthManager.getPermissions(...)` bypass on Save,
 * pinned by an integration test), validation rule and timer is the legacy
 * logic verbatim; only the rendering changed. Phase 6 moved the three BootBox
 * dialogs (rename warning, delete confirm, delete-failure notice) onto DS
 * Dialog — the § 76 pattern; the response fields that drive them, and what
 * confirming actually calls, are unchanged.
 */
const AddEditViewCategory = ({
  categoryName,
  setCategoryName,
  categoryTypeName,
  setCategoryTypeName,
  setCategoryDetailPageLoaded,
  showShimmer,
  setShowShimmer,
}) => {
  const {
    editingItem,
    setListReloadCounter,
    setEditingItem,
    categoryTypeList,
    categoryType,
    setCategoryType,
  } = useContext(CategoryContext);
  let api_base_url = AppSettings.GMS_BACKEND_API_URL;
  let [loading, setLoading] = React.useState(false);
  let [loadingInfo, setLoadingInfo] = React.useState(null);
  /* Empty means "render the DS ImagePlaceholder" (§ 191) — the state used to
     default to a hotlinked Wikipedia placeholder SVG. */
  let [categoryImage, setCategoryImage] = useState("");

  let [savingInfo, setsavingInfo] = React.useState({
    done: null,
    message: "",
  });

  const [disableButtons, setDisableButtons] = useState(true);
  /* The US 12394 rename warning (see handle_save_category) — the warning
     string while the acknowledge dialog is up, null otherwise. */
  const [renameWarning, setRenameWarning] = useState(null);

  let [errorMessage, setErrorMessage] = useState(null);
  let initialStateObject = {
    id: "",
    name: "",
    description: "",
    active: true,
    category_type_id: "",
    image_url: null
  };

  const validationOptions = {
    name: {
      required: true,
      minLength: 2,
      maxLength: 60,
      type: "alnum_with_spaces",
    },
    category_type_id: { required: true },
  };

  let [categoryDetail, setCategoryDetail] = useState(initialStateObject);
  let [categoryDetailCopy, setCategoryDetailCopy] =
    useState(initialStateObject);

  useEffect(() => {
    if (editingItem.id === "0") {
      setCategoryDetail(initialStateObject);
      setCategoryDetailCopy(initialStateObject);
      if (editingItem.category_type_id !== null) {
        setCategoryDetail({
          ...initialStateObject,
          category_type_id: editingItem.category_type_id,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem]);

  async function load_Category_detail() {
    setLoading(true);
    setLoadingInfo("Loading Category Details");
    let res = await ServiceWrapper.doGet(
      api_base_url + "category/details/" + editingItem.id,
      null,
      null,
      AuthManager.getGmsToken()
    );
    if (!res.errorfound) {
      await setCategoryDetail(res?.data?.data[0]);
      setCategoryType({
        type: "c",
        id: res?.data?.data[0]?.id,
      });
      await setCategoryDetailCopy(res.data.data[0]);
      const imageUrl = res.data.data[0].image_url;
      const timestamp = new Date().getTime();
      /* The ?timestamp cache-bust only applies to a REAL image; no URL means the
         placeholder renders instead of a fetch to a placeholder. */
      setCategoryImage(imageUrl ? `${imageUrl}?timestamp=${timestamp}` : "");

      setLoading((prev) => !prev);
      setLoadingInfo(null);
      setDisableButtons(true);
    } else {
      setLoading((prev) => !prev);
      setErrorMessage(
        "Error while fetching details, Please try after some time."
      );
    }
  }

  async function hande_change(e) {
    let temp = { ...categoryDetail };

    temp[e.target.name] =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setCategoryDetail(temp);
    if (e.target.name === "image_url") {
      setCategoryImage(e.target.value);
    }
  }

  useEffect(() => {
    const validation = Utils.validate(categoryDetail, validationOptions);
    if (!validation[0]) setDisableButtons(true);
    else if (
      JSON.stringify(categoryDetail) === JSON.stringify(initialStateObject)
    ) {
      setDisableButtons(true);
    } else if (
      JSON.stringify(categoryDetail) === JSON.stringify(categoryDetailCopy)
    ) {
      setDisableButtons(true);
    } else {
      setDisableButtons(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryDetail]);

  async function save_changes() {
    setDisableButtons(true);
    setsavingInfo(null);
    let final_url;
    final_url = `${api_base_url}category/edit/${editingItem.id}`;
    if (editingItem.id === "0") {
      final_url = `${api_base_url}category/create`;
    }

    let res = await ServiceWrapper.doPost(
      final_url,
      categoryDetail,
      null,
      AuthManager.getGmsToken()
    );
    if (!res.errorfound) {
      if (res.data.done) {
        setsavingInfo({
          done: true,
          message: res.data.message,
        });
        toast.success(res.data.message);
        // US 12394 / AC #4: a rename succeeds even when one or more models point at this
        // category, but those models are NOT auto-updated. The backend returns a generic
        // warning (used_in_models / warning) — surface it in an acknowledged dialog so the
        // modeler knows to manually update and re-promote each affected model.
        if (res.data.used_in_models && res.data.warning) {
          setRenameWarning(res.data.warning);
        }
        if (editingItem.id === "0") {
          handle_reload_list(res?.data?.id);
        } else {
          handle_reload_list(editingItem.id);
          load_Category_detail();
        }
      } else {
        toast.error(res.data.message);
        setDisableButtons(false);
      }
    } else {
      setDisableButtons(false);
      setsavingInfo({
        done: false,
        message: "Error while saving, Please try after some time.",
      });
    }

    setTimeout(() => {
      setsavingInfo({
        done: null,
        message: "",
      });
    }, 5000);
  }

  function handle_reload_list(id) {
    setEditingItem({
      type: "c",
      form: "view",
      id: id,
    });
    if (
      !(
        categoryDetail.name === categoryDetailCopy.name &&
        categoryDetail.active === categoryDetailCopy.active &&
        categoryDetail.category_type_id === categoryDetailCopy.category_type_id
      )
    ) {
      setListReloadCounter((n) => n + 1);
    }

    copy_categoryType_detail();
  }

  function copy_categoryType_detail() {
    let temp = JSON.parse(JSON.stringify(categoryDetail));
    setCategoryDetailCopy(temp);
  }

  useEffect(() => {
    if (editingItem.form === "edit" && editingItem.id !== "0") {
      load_Category_detail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem]);

  const handleOnSaveClick = () => {
    if (
      (editingItem.form === "edit" && editingItem.id !== "0" &&
        AuthManager.getPermissions("gmscategory_update"))
      || (editingItem.form === "edit" && editingItem.id === "0" &&
        AuthManager.getPermissions("gmscategory_create"))) {
      save_changes();
    } else {
      toast.info("You do not have permission to save the category");
    }
  }

  const handleOnCancelClick = () => {
    setCategoryDetailPageLoaded(true);
    setCategoryImage("");
    // AB#15668: a new category has nothing of its own to return to, so Cancel goes
    // back to the category on view before Create (categoryType). That context is now
    // set whenever a category's view loads (CategoryDetails), so it is present after
    // a /gms/categories/c/<id> deep link too; if nothing was on view, clear the
    // selection instead of throwing on a null categoryType.
    if (editingItem.id === "0") {
      setEditingItem(
        categoryType?.id
          ? { type: categoryType.type, form: "view", id: categoryType.id }
          : null,
      );
    } else {
      setEditingItem({ type: "c", form: "view", id: categoryDetail.id });
    }
  }

  return (
    <React.Fragment>
      {/* US 12394 / AC #4 (was BootBox): a rename succeeds even when models
          point at this category, but those models are NOT auto-updated — the
          modeler must acknowledge before moving on, so there is no header ✕
          and no scrim-dismiss; Escape still works (a11y floor). */}
      <Dialog
        isOpen={renameWarning !== null}
        onClose={() => setRenameWarning(null)}
        title="Category Renamed — Action Needed"
        tone="warning"
        showCloseButton={false}
        data-testid="category-rename-warning-dialog"
        footer={
          <div className="tw-flex tw-w-full tw-items-center tw-justify-end tw-gap-3">
            <Button
              variant="primary"
              data-testid="category-rename-warning-ok-btn"
              onClick={() => setRenameWarning(null)}
            >
              OK, I got it
            </Button>
          </div>
        }
      >
        <Typography variant="body-primary" className="tw-text-ink">{renameWarning}</Typography>
      </Dialog>
      {editingItem.form === "view" ? (
        <CategoryDetails
          setCategoryName={setCategoryName}
          categoryTypeName={categoryTypeName}
          setCategoryTypeName={setCategoryTypeName}
          setCategoryDetailPageLoaded={setCategoryDetailPageLoaded}
          setCategoryDetail1={setCategoryDetail}
          setShowShimmer={setShowShimmer}
          showShimmer={showShimmer}
        />
      ) : (
        <>
          {loading && (
            <div className="tw-space-y-4">
              <Skeleton variant="rect" width="25%" height="20px" />
              <div className="tw-border tw-border-edge tw-rounded-ctl tw-p-4 tw-space-y-4">
                <div className="tw-flex tw-justify-between">
                  <Skeleton variant="rect" width="30%" height="24px" />
                  <Skeleton variant="rect" width="15%" height="24px" />
                </div>
                <div className="tw-flex tw-gap-4">
                  <div className="tw-flex-1 tw-space-y-4">
                    <Skeleton variant="rect" width="100%" height="36px" />
                    <Skeleton variant="rect" width="100%" height="36px" />
                    <Skeleton variant="rect" width="100%" height="36px" />
                    <Skeleton variant="rect" width="100%" height="80px" />
                  </div>
                  <Skeleton variant="rect" width="150px" height="150px" />
                </div>
              </div>
            </div>
          )}
          {!loading &&
            (errorMessage ? (
              <Alert tone="danger" live data-testid="category-form-error-alert">
                {errorMessage}
              </Alert>
            ) : (
              <div className="mt-1">
                <Breadcrumbs
                  className="tw-ms-4 tw-my-2"
                  data-testid="category-form-breadcrumbs"
                  items={[
                    { label: "Taxonomy" },
                    {
                      /* Clickable crumb — jumps to the parent type's view.
                         onClick without href renders a <button> (§ 69). */
                      label: categoryTypeName ?? "",
                      onClick: () => {
                        setEditingItem({
                          type: "ct",
                          form: "view",
                          id: categoryDetail?.category_type_id,
                        });
                      },
                    },
                    { label: categoryName ?? "" },
                  ]}
                />
                <Card className="tw-mb-5">
                  <div className="d-flex justify-content-between">
                    <div>
                      <Typography variant="heading-2" as="h2" className="tw-text-ink">
                        {`${editingItem.id !== "0" ? "Edit " : "Create "}Category`}
                      </Typography>
                      {editingItem.id !== "0" && (
                        <p className="tw-flex tw-items-center tw-gap-2 tw-mt-1 tw-mb-0">
                          <Typography variant="sub-title-label" as="span" className="tw-text-ink">
                            UID
                          </Typography>
                          <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary">
                            {`${editingItem.id !== "0" ? editingItem.id : ""}`}
                          </Typography>
                        </p>
                      )}
                    </div>

                    <div className="tw-flex tw-gap-2 tw-items-start tw-mt-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleOnCancelClick}
                        data-testid="category-cancel-btn"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={disableButtons}
                        onClick={handleOnSaveClick}
                        data-testid="category-save-btn"
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                  <form>
                    <div className="row mt-3">
                      <div className="col-sm-10 tw-space-y-4">
                        <Dropdown
                          label="Category Type"
                          id="category_type_id"
                          data-testid="category-addeditviewcategory-select-1"
                          placeholder="Select Category Type"
                          /* The full industry list (getbyindustryid, unpaged) — long enough to
                             scroll, so it filters locally like the category switcher does. */
                          searchable
                          searchPlaceholder="Search category types"
                          options={categoryTypeList.map((ct) => ({
                            value: ct.id,
                            label: ct.name,
                          }))}
                          value={categoryDetail?.category_type_id || undefined}
                          /* hande_change keys off target.name/.value — hand it the
                             same shape the native <select> produced (§ 72 wiring). */
                          onChange={(value) =>
                            hande_change({
                              target: {
                                name: "category_type_id",
                                value,
                                type: "select-one",
                              },
                            })
                          }
                          error={!categoryDetail?.category_type_id}
                        />

                        <TextField
                          label="Name"
                          id="name"
                          name="name"
                          data-testid="category-addeditviewcategory-input-2"
                          placeholder="Enter Category Name"
                          value={categoryDetail.name || ""}
                          onChange={(_, e) => hande_change(e)}
                          maxLength={60}
                          minLength={2}
                          error={!(categoryDetail?.name?.trim().length >= 2)}
                          helperText="Name must be 2-60 characters long, contain letters, numbers and spaces, and must not contain special characters."
                        />

                        <TextField
                          label="Image Url"
                          id="imageUrl"
                          name="image_url"
                          data-testid="category-addeditviewcategory-input-3"
                          placeholder="Enter image Url"
                          value={categoryDetail.image_url || ""}
                          onChange={(_, e) => hande_change(e)}
                        />

                        <Textarea
                          label="Description"
                          id="description"
                          name="description"
                          data-testid="category-addeditviewcategory-textarea-4"
                          rows={3}
                          placeholder="Enter description"
                          onChange={(_, e) => hande_change(e)}
                          value={categoryDetail.description || ""}
                        />
                      </div>

                      <div className="col col-sm-2 mt-4">
                        {categoryImage ? (
                          <img
                            src={categoryImage}
                            className="img-fluid tw-border tw-border-edge tw-rounded-ctl"
                            key={new Date().getTime()}
                            alt="Category preview"
                            /* A URL that fails to load falls back to the placeholder
                               instead of the browser's broken-image glyph. */
                            onError={() => setCategoryImage("")}
                          />
                        ) : (
                          <ImagePlaceholder
                            width="100%"
                            height="150px"
                            data-testid="category-image-placeholder"
                          />
                        )}
                      </div>
                    </div>

                    <div className="float-start tw-mt-3">
                      {savingInfo !== null ? (
                        <>
                          {savingInfo.done ? (
                            <Alert tone="success" live data-testid="category-save-status-alert">
                              {savingInfo.message}
                            </Alert>
                          ) : (
                            <>
                              {savingInfo.done === false && (
                                <Alert tone="danger" live data-testid="category-save-status-alert">
                                  {savingInfo.message}
                                </Alert>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <Alert tone="info" live data-testid="category-save-status-alert">
                          <span className="tw-inline-flex tw-items-center tw-gap-2">
                            <Spinner size="sm" aria-hidden="true" />
                            <span>Saving your Changes</span>
                          </span>
                        </Alert>
                      )}
                    </div>
                  </form>
                </Card>
              </div>
            ))}
        </>
      )}
    </React.Fragment>
  );
};
export default AddEditViewCategory;

const CategoryDetails = ({
  setCategoryName,
  categoryTypeName,
  setCategoryTypeName,
  setCategoryDetailPageLoaded,
  setCategoryDetail1,
  setShowShimmer,
  showShimmer,
}) => {
  const navigate = useNavigate();
  let api_base_url = AppSettings.GMS_BACKEND_API_URL;

  const appData = useContext(AppContext);

  const { editingItem, setEditingItem, setCategoryType } = useContext(CategoryContext);
  let [loading, setLoading] = React.useState({
    status: "",
    message: "",
  });
  let [categoryDetail, setCategoryDetail] = useState({});
  /* Empty means the DS ImagePlaceholder (§ 191), as in the form above. */
  let [categoryImage, setCategoryImage] = useState("");

  const [clipText, setClipText] = useState(null);

  /* The three popovers of this view (was Bootstrap dropdowns): the header
     Create menu, the error-state Create menu, and the kebab. Each owns its
     mount and dismisses via useDismissable (§ 76 pattern). */
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const createMenuRef = useRef(null);
  useDismissable({
    isOpen: createMenuOpen,
    onDismiss: () => setCreateMenuOpen(false),
    isInside: (target) => !!createMenuRef.current?.contains(target),
  });
  const [errorCreateMenuOpen, setErrorCreateMenuOpen] = useState(false);
  const errorCreateMenuRef = useRef(null);
  useDismissable({
    isOpen: errorCreateMenuOpen,
    onDismiss: () => setErrorCreateMenuOpen(false),
    isInside: (target) => !!errorCreateMenuRef.current?.contains(target),
  });
  const [kebabOpen, setKebabOpen] = useState(false);
  const kebabRef = useRef(null);
  useDismissable({
    isOpen: kebabOpen,
    onDismiss: () => setKebabOpen(false),
    isInside: (target) => !!kebabRef.current?.contains(target),
  });
  /* Phase 6 (was BootBox): the delete confirm and the delete-failure notice. */
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteFailure, setDeleteFailure] = useState(null);

  /* "UID Copied" dismisses itself after 2s (user-requested 2026-09-01 — it
     used to stay until the component unmounted). The ref resets the timer on
     a repeat click; the effect clears it on unmount. */
  const clipTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(clipTimerRef.current), []);

  function handle_clipbord(text) {
    navigator.clipboard.writeText(text).then(() => {
      setClipText(text);
      clearTimeout(clipTimerRef.current);
      clipTimerRef.current = setTimeout(() => setClipText(null), 2000);
    });
  }

  function handle_url_clipbord(id) {
    const url = `${window.location.origin}${window.location.pathname}`;
    const categoryID = `/c/${id}`;
    if (url.includes(id)) {
      navigator.clipboard
        .writeText(`${url}`)
        .then(() => toast.success("Url Copied to clipboard"));
    } else {
      navigator.clipboard
        .writeText(`${url}${categoryID}`)
        .then(() => toast.success("Url Copied to clipboard"));
    }
  }

  useEffect(() => {
    load_Category_detail();
    setClipText(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem.id]);

  const { setListReloadCounter, setDeleteInfo, categoryTypeList } = useContext(CategoryContext);

  async function deleteCategory(id) {
    toast.info("Preparing to delete category");

    let res = await ServiceWrapper.doGet(
      api_base_url + "category/delete/" + id,
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (!res.errorfound) {
      if (res.data.done) {
        toast.success(res.data.message);
        if (
          editingItem !== null &&
          editingItem !== undefined &&
          editingItem.type === "c" &&
          editingItem.id === id
        ) {
          setEditingItem(null);
        }
        setListReloadCounter((n) => n + 1);
      } else {
        setDeleteInfo({
          status: "Error",
          message: "Deletion of Category Failed",
        });

        setDeleteFailure(res.data.message);
      }
    } else {
      setDeleteInfo({
        status: "Error",
        message: "Error while deleting selected category!!",
      });
    }

    setTimeout(() => {
      setDeleteInfo({
        status: null,
        message: "",
      });
    }, 3000);
  }

  // The category the view is meant to show right now. A details response for an
  // earlier selection (click A, then B quickly; A answers last) is dropped, or it
  // would put A on view, and into categoryType, while B is selected.
  const viewedIdRef = useRef(editingItem?.id);
  viewedIdRef.current = editingItem?.id;

  async function load_Category_detail() {
    const requestedId = editingItem.id;
    setShowShimmer(true);
    setCategoryDetail(null);
    setLoading({ status: "loading", message: "Loading Category Details" });
    let res = await ServiceWrapper.doGet(
      `${api_base_url}category/details/${requestedId}`,
      null,
      null,
      AuthManager.getGmsToken()
    );
    if (requestedId !== viewedIdRef.current) return;
    if (!res.errorfound) {
      setCategoryDetailPageLoaded(true);
      setShowShimmer(false);
      setLoading({ status: "success", message: "" });
      setCategoryDetail(res?.data?.data[0]);
      // AB#15668 / AB#15670: the context's categoryType is "the category on view".
      // It used to be set only by a tree click or the list's initial auto-select,
      // so it stayed null after a deep link (Create -> Cancel then threw) and stale
      // after a save (a click on the previously viewed category was ignored as a
      // re-click). Keep it in step with whatever this view actually loaded.
      if (res?.data?.data[0]?.id) {
        setCategoryType({ type: "c", id: res.data.data[0].id });
      }
      const imageUrl = res?.data?.data[0]?.image_url;
      const timestamp = new Date().getTime();
      setCategoryImage(imageUrl ? `${imageUrl}?timestamp=${timestamp}` : "");

      setCategoryName(res?.data?.data[0]?.name);
    } else {
      setShowShimmer(false);
      setCategoryDetailPageLoaded(false);
      setLoading({ status: "Error", message: "Error while loading details" });
    }
  }

  const categoryContextData = useContext(CategoryContext);
  const categoryType = loading.status === "success" && categoryDetail
    ? categoryTypeList.find(type => type.id === categoryDetail.category_type_id)?.name || ""
    : "";

  useEffect(() => {
    if (categoryType) setCategoryTypeName(categoryType)
  }, [categoryType])

  /* One Create menu body, mounted twice (header + error state) with distinct
     testids — the two never share a click but can share the handlers. */
  const createMenuItems = (idPrefix) => [
    {
      id: "category-type",
      label: "Category Type",
      "data-testid": `${idPrefix}-create-category-type-item`,
      onClick: () => {
        setCreateMenuOpen(false);
        setErrorCreateMenuOpen(false);
        if (appData.pm.checkPermission("gmscategory_create")) {
          categoryContextData.setEditingItem({
            type: "ct",
            form: "edit",
            id: "0",
          });
        } else {
          toast.info(
            "You don't have permission to create the category type"
          );
        }
      },
    },
    {
      id: "category",
      label: "Category",
      "data-testid": `${idPrefix}-create-category-item`,
      onClick: () => {
        setCreateMenuOpen(false);
        setErrorCreateMenuOpen(false);
        if (appData.pm.checkPermission("gmscategory_create")) {
          categoryContextData.setEditingItem({
            type: "c",
            form: "edit",
            id: "0",
            category_type_id: undefined,
          });
        } else {
          toast.info("You don't have permission to create the category");
        }
      },
    },
  ];

  return (
    <>
      {/* Delete confirm (was BootBoxManager.confirm with an empty title):
          same legacy copy in the body, same callback — deleteCategory(id). */}
      <Dialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete category?"
        tone="danger"
        data-testid="category-delete-confirm-dialog"
        footer={
          <div className="tw-flex tw-w-full tw-items-center tw-justify-end tw-gap-3">
            <Button
              variant="secondary"
              data-testid="category-delete-cancel-btn"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger-primary"
              data-testid="category-delete-confirm-btn"
              onClick={() => {
                setDeleteConfirmOpen(false);
                deleteCategory(editingItem.id);
              }}
            >
              Confirm
            </Button>
          </div>
        }
      >
        <Typography variant="body-primary" className="tw-text-ink">
          Do you really want to delete <strong>{categoryDetail?.name}</strong>?
        </Typography>
        <Typography variant="body-primary" className="tw-mt-2 tw-text-ink-secondary">
          This cannot be undone.
        </Typography>
      </Dialog>
      {/* Delete-failure notice (was a BootBox acknowledge dialog). */}
      <Dialog
        isOpen={deleteFailure !== null}
        onClose={() => setDeleteFailure(null)}
        title="Category Deletion Failure"
        tone="danger"
        showCloseButton={false}
        data-testid="category-delete-failure-dialog"
        footer={
          <div className="tw-flex tw-w-full tw-items-center tw-justify-end tw-gap-3">
            <Button
              variant="primary"
              data-testid="category-delete-failure-ok-btn"
              onClick={() => setDeleteFailure(null)}
            >
              OK, I got it
            </Button>
          </div>
        }
      >
        <Typography variant="body-primary" className="tw-text-ink">
          We are Sorry, We could not delete the selected category Below is more
          information.
        </Typography>
        <Typography variant="body-primary" className="tw-mt-2 tw-text-ink">
          <strong>{deleteFailure}</strong>
        </Typography>
      </Dialog>
      <div className="d-flex flex-column justify-content-between">
        <div className="d-flex justify-content-between ps-0 pe-0 pb-2 tw-border-b tw-border-edge">
          <div className="ps-4 mt-2">
            <Typography variant="heading-2" as="h2" className="tw-text-ink">
              Categories
            </Typography>
          </div>
          <div ref={createMenuRef} className="tw-relative me-2">
            <Button
              variant="secondary"
              size="sm"
              data-testid="category-addeditviewcategory-button-5"
              aria-haspopup="menu"
              aria-expanded={createMenuOpen}
              onClick={() => setCreateMenuOpen((open) => !open)}
            >
              Create
            </Button>
            {createMenuOpen && (
              <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-z-50 tw-w-44">
                <Menu
                  variant="compact"
                  data-testid="category-detail-create-menu"
                  items={createMenuItems("category-detail")}
                />
              </div>
            )}
          </div>
        </div>

        <Breadcrumbs
          className="tw-px-4 tw-py-3"
          data-testid="category-detail-breadcrumbs"
          items={[
            { label: "Taxonomy" },
            {
              label: categoryTypeName,
              onClick: () => {
                setEditingItem({
                  type: "ct",
                  form: "view",
                  id: categoryDetail?.category_type_id,
                });
              },
            },
            ...(categoryTypeName
              ? [{ label: categoryDetail ? categoryDetail?.name : "" }]
              : []),
          ]}
        />

        {showShimmer && (
          <div className="d-flex flex-wrap">
            <div className="col-2 ps-5 ">
              <SkeletonElement type={"thumbnail"} />
            </div>
            <div className="col-10">
              <SkeletonElement type={"title"} />
              <SkeletonElement type={"text"} />
              <br />
              <SkeletonElement type={"text50"} />
              <SkeletonElement type={"text50"} />
            </div>
          </div>
        )}

        {loading.status === "Error" && (
          <div className="col-12 ps-0 pe-0 tw-flex-grow">
            <EmptyState
              size="md"
              className="mt-5"
              data-testid="category-detail-empty-state"
              illustration={<NoDataIllustration />}
              title="No data found"
              description="You can create category for the products."
              action={
                <div ref={errorCreateMenuRef} className="tw-relative tw-inline-block">
                  <Button
                    variant="secondary"
                    data-testid="category-addeditviewcategory-button-6"
                    aria-haspopup="menu"
                    aria-expanded={errorCreateMenuOpen}
                    onClick={() => setErrorCreateMenuOpen((open) => !open)}
                  >
                    Create Category
                  </Button>
                  {errorCreateMenuOpen && (
                    <div className="tw-absolute tw-left-1/2 -tw-translate-x-1/2 tw-top-full tw-mt-1 tw-z-50 tw-w-44">
                      <Menu
                        variant="compact"
                        data-testid="category-detail-error-create-menu"
                        items={createMenuItems("category-detail-error")}
                      />
                    </div>
                  )}
                </div>
              }
            />
          </div>
        )}

        {loading.status === "success" && categoryDetail && (
          <Card className="tw-mx-2 tw-mb-3">
            {/* One top-aligned media+text row. The legacy bootstrap col-2/col-10 grid
                and its conditional pb-5 / height:165px wrapper existed to size the
                old remote placeholder image — both branches are a fixed 150×150 now. */}
            <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-5">
              <div className="tw-shrink-0">
                {categoryImage ? (
                  <img
                    src={categoryImage}
                    className="tw-border tw-border-edge tw-rounded-ctl tw-object-contain"
                    key={new Date().getTime()}
                    style={{
                      height: "150px",
                      width: "150px",
                      backgroundColor: "transparent",
                    }}
                    alt={categoryDetail.name || "Category"}
                    loading="lazy"
                    onError={() => setCategoryImage("")}
                  />
                ) : (
                  <ImagePlaceholder
                    width="150px"
                    height="150px"
                    data-testid="category-detail-image-placeholder"
                  />
                )}
              </div>

              <div className="tw-flex-1 tw-min-w-0">
                <div className="d-flex justify-content-between">
                  <div className="mb-0 w-auto">
                    <Typography variant="heading-2" as="h2" className="tw-text-ink">
                      {categoryDetail.name || ""}
                    </Typography>
                    <Typography variant="body-primary" as="p" className="tw-mt-1 tw-mb-0 tw-text-ink">
                      {categoryDetail.description}
                    </Typography>
                  </div>

                  <div className="w-auto tw-flex tw-items-center tw-gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      data-testid="category-edit-btn"
                      onClick={() => {
                        if (
                          appData.pm.checkPermission("gmscategory_update")
                        ) {
                          setLoading((prve) => !prve);
                          setEditingItem({
                            type: "c",
                            form: "edit",
                            id: categoryDetail.id,
                          });
                        } else {
                          toast.info("You don't have permission to edit");
                        }
                      }}
                    >
                      Edit
                    </Button>
                    <div ref={kebabRef} className="tw-relative">
                      <button
                        type="button"
                        id="btnGroupDrop1"
                        data-testid="category-addeditviewcategory-i-1"
                        aria-haspopup="menu"
                        aria-expanded={kebabOpen}
                        aria-label="More actions"
                        onClick={() => setKebabOpen((open) => !open)}
                        className={`tw-bg-transparent tw-border-0 tw-p-1 tw-rounded-ctl tw-text-ink-secondary hover:tw-text-ink hover:tw-bg-surface-hover tw-transition-colors ${FOCUS_RING}`}
                      >
                        <MoreVertical size={16} aria-hidden="true" />
                      </button>
                      {kebabOpen && (
                        <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-z-50 tw-w-36">
                          <Menu
                            variant="compact"
                            data-testid="category-overflow-menu"
                            items={[
                              {
                                id: "delete",
                                label: "Delete",
                                tone: "danger",
                                "data-testid": "category-delete-menu-item",
                                onClick: () => {
                                  setKebabOpen(false);
                                  if (
                                    appData.pm.checkPermission(
                                      "gmscategory_delete"
                                    )
                                  ) {
                                    setDeleteConfirmOpen(true);
                                  } else {
                                    toast.info(
                                      "You don't have permission to delete the category"
                                    );
                                  }
                                },
                              },
                              {
                                id: "share-url",
                                label: "Share URL",
                                "data-testid": "category-share-url-menu-item",
                                onClick: () => {
                                  setKebabOpen(false);
                                  handle_url_clipbord(categoryDetail.id);
                                },
                              },
                            ]}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <p className="tw-flex tw-items-center tw-gap-2 tw-mt-4 tw-mb-1">
                  <Typography variant="title" as="span" className="tw-text-ink">
                    UID
                  </Typography>
                  <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">
                    {`${categoryDetail.id || ""}`}
                  </Typography>
                  <button
                    type="button"
                    data-testid="category-addeditviewcategory-i-2"
                    aria-label="Copy UID"
                    onClick={() => handle_clipbord(categoryDetail.id)}
                    className={`tw-bg-transparent tw-border-0 tw-p-1 tw-rounded-ctl tw-text-accent hover:tw-bg-surface-hover tw-transition-colors ${FOCUS_RING}`}
                  >
                    <Copy size={14} aria-hidden="true" />
                  </button>
                  {clipText !== null && (
                    <Typography variant="caption-metadata" as="span" className="tw-text-tone-success-ink">
                      UID Copied
                    </Typography>
                  )}
                </p>

                <p className="small mb-3 mt-2">
                  <Link
                    to="#"
                    data-testid="category-view-products-link"
                    className={`tw-text-body-primary tw-text-accent hover:tw-text-accent-hover tw-no-underline tw-rounded-ctl ${FOCUS_RING}`}
                    onClick={() => {
                      navigate("/gms/products/list/" + categoryDetail.id);
                    }}
                  >
                    {"View Products  >"}
                  </Link>
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </>
  );
};
